/**
 * Derive the net position effect of completed futures fills for display.
 *
 * LumiBot's existing trades CSV records side and filled quantity, but it does
 * not currently record an explicit open/close action. A completed backtest is
 * a closed sequence, so React can replay each symbol from flat and describe
 * the fill's actual effect without changing broker or runtime behavior.
 */
import {
  isTerminalTradeEventStatus,
  normalizeTradeStatus,
} from '../../utils/tradeLifecycleStatus';

export type FuturesPositionAction =
  | 'open_long'
  | 'close_long'
  | 'open_short'
  | 'close_short'
  | 'reverse_to_long'
  | 'reverse_to_short';

export type BacktestTrade = {
  time?: string | number | null;
  side?: string | null;
  price?: number | string | null;
  qty?: number | string | null;
  status?: string | null;
  symbol?: string | null;
  assetType?: string | null;
  [key: string]: unknown;
};

export type NormalizedFuturesTrade = BacktestTrade & {
  time: string | number;
  symbol: string;
  side: string;
  qty: number;
  price: number;
  status: string;
  positionAction: FuturesPositionAction;
  positionBefore: number;
  positionAfter: number;
  openingQuantity: number;
  closingQuantity: number;
};

const FUTURES_ASSET_TYPES = new Set([
  'future',
  'futures',
  'crypto_future',
  'crypto_futures',
  'cont_future',
  'continuous_future',
]);

const BUY_SIDES = new Set([
  'buy',
  'buy_to_open',
  'buy_to_close',
  'buy_to_cover',
]);

const SELL_SIDES = new Set([
  'sell',
  'sell_short',
  'sell_to_open',
  'sell_to_close',
]);

const roundPosition = (value: number): number => {
  const rounded = Number(value.toFixed(12));
  return Object.is(rounded, -0) ? 0 : rounded;
};

const finiteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(String(value).replace(/[$,]/g, ''));
  return Number.isFinite(number) ? number : null;
};

const normalizedAssetType = (value: unknown): string => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
);

export const isFuturesBacktestTrade = (trade: BacktestTrade): boolean => (
  FUTURES_ASSET_TYPES.has(normalizedAssetType(trade.assetType))
);

export const futuresPositionActionLabel = (
  action: FuturesPositionAction,
): string => ({
  open_long: 'Open long',
  close_long: 'Close long',
  open_short: 'Open short',
  close_short: 'Close short',
  reverse_to_long: 'Reverse to long',
  reverse_to_short: 'Reverse to short',
})[action];

const positionEffect = (
  before: number,
  signedQuantity: number,
): Pick<NormalizedFuturesTrade, 'positionAction' | 'openingQuantity' | 'closingQuantity'> => {
  const after = roundPosition(before + signedQuantity);
  const quantity = Math.abs(signedQuantity);

  if (signedQuantity > 0) {
    if (before < 0 && after > 0) {
      return {
        positionAction: 'reverse_to_long',
        closingQuantity: Math.abs(before),
        openingQuantity: after,
      };
    }
    if (before < 0) {
      return {
        positionAction: 'close_short',
        closingQuantity: Math.min(Math.abs(before), quantity),
        openingQuantity: 0,
      };
    }
    return {
      positionAction: 'open_long',
      closingQuantity: 0,
      openingQuantity: quantity,
    };
  }

  if (before > 0 && after < 0) {
    return {
      positionAction: 'reverse_to_short',
      closingQuantity: before,
      openingQuantity: Math.abs(after),
    };
  }
  if (before > 0) {
    return {
      positionAction: 'close_long',
      closingQuantity: Math.min(before, quantity),
      openingQuantity: 0,
    };
  }
  return {
    positionAction: 'open_short',
    closingQuantity: 0,
    openingQuantity: quantity,
  };
};

/**
 * Replays complete futures fills by symbol from a flat starting position.
 * Invalid or non-terminal rows are ignored because including them would make
 * every later position label misleading.
 */
export const normalizeFuturesTrades = (
  trades: BacktestTrade[] = [],
): NormalizedFuturesTrade[] => {
  const validTrades = trades
    .map((trade, sourceIndex) => ({ trade, sourceIndex }))
    .filter(({ trade }) => {
      const status = normalizeTradeStatus(trade.status);
      const quantity = finiteNumber(trade.qty);
      const price = finiteNumber(trade.price);
      const side = String(trade.side || '').trim().toLowerCase();
      const time = new Date(String(trade.time || '')).getTime();
      return isFuturesBacktestTrade(trade)
        && isTerminalTradeEventStatus(status)
        && quantity !== null
        && quantity > 0
        && price !== null
        && Number.isFinite(time)
        && (BUY_SIDES.has(side) || SELL_SIDES.has(side))
        && Boolean(String(trade.symbol || '').trim());
    })
    .sort((left, right) => {
      const leftTime = new Date(String(left.trade.time)).getTime();
      const rightTime = new Date(String(right.trade.time)).getTime();
      return leftTime - rightTime || left.sourceIndex - right.sourceIndex;
    });

  const positions = new Map<string, number>();

  return validTrades.map(({ trade }) => {
    const symbol = String(trade.symbol).trim();
    const side = String(trade.side).trim().toLowerCase();
    const quantity = finiteNumber(trade.qty) as number;
    const price = finiteNumber(trade.price) as number;
    const status = normalizeTradeStatus(trade.status);
    const before = positions.get(symbol) || 0;
    const signedQuantity = BUY_SIDES.has(side) ? quantity : -quantity;
    const after = roundPosition(before + signedQuantity);
    const effect = positionEffect(before, signedQuantity);
    positions.set(symbol, after);

    return {
      ...trade,
      time: trade.time as string | number,
      symbol,
      side,
      qty: quantity,
      price,
      status,
      positionBefore: before,
      positionAfter: after,
      openingQuantity: roundPosition(effect.openingQuantity),
      closingQuantity: roundPosition(effect.closingQuantity),
      positionAction: effect.positionAction,
    };
  });
};
